import { useCallback, useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useI18n } from "@/i18n/I18nProvider";
import { COMPANIES } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Search, Building2, Trash2, Upload, Download, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";

const PAGE_SIZE = 60;

export default function CompaniesPage() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const canManage = user?.role === "super_admin" || user?.role === "operatore";
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", vat_number: "", tax_code: "", address: "", email: "", emails_text: "", phone: "", referent: "", logo_url: "" });
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  // Debounce search 300ms → riduce chiamate di rete
  useEffect(() => {
    const h = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(h);
  }, [q]);
  // Reset page quando cambia la ricerca
  useEffect(() => {
    setPage(1);
  }, [debouncedQ]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (debouncedQ.trim()) params.search = debouncedQ.trim();
      const r = await api.get("/companies", { params });
      // Nuovo formato envelope
      setList(r.data.items || []);
      setTotal(r.data.total || 0);
      setPages(r.data.pages || 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  }, [page, debouncedQ]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const emails = (form.emails_text || "").split(/[,;\n]+/).map((s) => s.trim()).filter((s) => s.includes("@"));
      const { emails_text, ...rest } = form;
      await api.post("/companies", { ...rest, emails });
      setOpen(false);
      setForm({ name: "", vat_number: "", tax_code: "", address: "", email: "", emails_text: "", phone: "", referent: "", logo_url: "" });
      toast.success(t("save"));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const remove = async (e, id) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm(lang === "it" ? "Eliminare questa azienda?" : "Delete this company?")) return;
    try {
      await api.delete(`/companies/${id}`);
      toast.success(t("delete"));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const importExcel = async (file) => {
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/companies/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${lang === "it" ? "Importate" : "Imported"}: ${r.data.inserted} · ${lang === "it" ? "duplicate" : "skipped"}: ${r.data.skipped}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const downloadTemplate = async () => {
    const r = await api.get("/companies/export/template", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = "aziende_template.xlsx"; a.click();
    URL.revokeObjectURL(url);
  };

  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);

  return (
    <div data-testid={COMPANIES.root} className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("companies")}</h1>
          <p className="text-muted-foreground text-sm mt-1" data-testid="companies-count">
            {loading ? "…" : `${total} ${t("companies").toLowerCase()}`}
            {debouncedQ.trim() && !loading && ` · ${lang === "it" ? "filtrate" : "filtered"}: "${debouncedQ.trim()}"`}
          </p>
        </div>
        <div className="flex gap-3 items-center flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("search")}
              className="pl-9 h-10 w-64"
              data-testid="companies-search"
            />
          </div>
          <Button variant="outline" onClick={downloadTemplate} data-testid="company-template-button" className="rounded-full btn-lift">
            <Download className="w-4 h-4 mr-1.5" /> {t("download_template")}
          </Button>
          {canManage && (
            <>
              <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="company-import-button" className="rounded-full btn-lift">
                <Upload className="w-4 h-4 mr-1.5" /> {importing ? "…" : t("import_excel")}
              </Button>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" hidden onChange={(e) => e.target.files?.[0] && importExcel(e.target.files[0])} />
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button data-testid={COMPANIES.createBtn} className="rounded-full btn-lift">
                    <Plus className="w-4 h-4 mr-1.5" /> {t("add_company")}
                  </Button>
                </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>{t("add_company")}</DialogTitle></DialogHeader>
              <form onSubmit={submit} data-testid={COMPANIES.form} className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>{t("company_name")}</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid={COMPANIES.name} />
                </div>
                <div><Label>{t("vat_number")}</Label><Input value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} data-testid={COMPANIES.vat} /></div>
                <div><Label>{t("tax_code")}</Label><Input value={form.tax_code} onChange={(e) => setForm({ ...form, tax_code: e.target.value })} data-testid={COMPANIES.tax} /></div>
                <div className="col-span-2"><Label>{t("address")}</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                <div><Label>{t("email")} <span className="text-muted-foreground text-[10px]">(primaria)</span></Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid={COMPANIES.email} /></div>
                <div><Label>{t("phone")}</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div className="col-span-2">
                  <Label>{lang === "it" ? "Altre email per promemoria (separate da virgola o riga)" : "Additional emails for reminders (comma or newline separated)"}</Label>
                  <Textarea rows={2} value={form.emails_text} onChange={(e) => setForm({ ...form, emails_text: e.target.value })} data-testid="company-emails-input" placeholder="ufficio@azienda.it, sicurezza@azienda.it" />
                </div>
                <div><Label>{t("referent")}</Label><Input value={form.referent} onChange={(e) => setForm({ ...form, referent: e.target.value })} /></div>
                <div><Label>{t("logo_url")}</Label><Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://... o carica dopo la creazione" /></div>
                <div className="col-span-2 text-[11px] text-muted-foreground -mt-2">
                  {lang === "it"
                    ? "Suggerimento: crea prima l'azienda, poi apri la sua pagina di dettaglio per caricare il logo come file."
                    : "Tip: create the company first, then open its detail page to upload the logo file."}
                </div>
                <DialogFooter className="col-span-2">
                  <Button type="submit" data-testid={COMPANIES.submit} className="rounded-full btn-lift">{t("save")}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
            </>
          )}
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" /> {lang === "it" ? "Caricamento…" : "Loading…"}
        </div>
      )}

      {!loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((c) => (
            <Link
              key={c.id} to={`/companies/${c.id}`}
              data-testid={COMPANIES.row(c.id)}
              className="relative bg-card border border-border rounded-lg p-5 card-hover block"
            >
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-md bg-muted grid place-items-center shrink-0 overflow-hidden">
                  {c.logo_url ? <img src={c.logo_url} alt="" className="w-full h-full object-cover" /> : <Building2 className="w-5 h-5 text-muted-foreground" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate pr-6">{c.name}</div>
                  <div className="text-xs text-muted-foreground mt-1">P.IVA {c.vat_number || "—"}</div>
                  <div className="text-xs text-muted-foreground truncate mt-0.5">{c.email || c.phone || c.address || "—"}</div>
                </div>
              </div>
              {canManage && (
                <button
                  onClick={(e) => remove(e, c.id)}
                  data-testid={`delete-company-${c.id}`}
                  title={t("delete")}
                  className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive p-1 rounded transition-opacity"
                  style={{ opacity: 1 }}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </Link>
          ))}
          {list.length === 0 && <div className="text-muted-foreground text-sm col-span-full text-center py-8">{t("no_data")}</div>}
        </div>
      )}

      {/* Paginator */}
      {!loading && total > PAGE_SIZE && (
        <div className="flex items-center justify-between border-t border-border pt-4" data-testid="companies-paginator">
          <div className="text-xs text-muted-foreground">
            {lang === "it" ? `Aziende ${from}–${to} di ${total}` : `Companies ${from}–${to} of ${total}`}
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className="rounded-full"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              data-testid="companies-prev"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="px-3 text-sm" data-testid="companies-page-indicator">
              {page} / {pages}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="rounded-full"
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              disabled={page >= pages}
              data-testid="companies-next"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
